/**
 * useCircularList – custom hook to manage the circular list data and functions.
 */
import DeviceConfig from '@devices/config';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import Screen from '@screen';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Constants
 *
 * POST_WRAP_BLOCK_UNTIL: The time in milliseconds to block key presses after a wrap. (60 milliseconds)
 * RENDER_WINDOW_RADIUS: neighbors within this many positions of the current index render for
 * real; everything else becomes a same-sized placeholder, so only currently-visible items stay
 * mounted.
 */
const POST_WRAP_BLOCK_UNTIL = 60;
const RENDER_WINDOW_RADIUS = 1;

/**
 * Props for the CircularList component.
 */
export interface CircularListProps<T> {
  direction?: Direction;
  duration: number;
  focus?: Focus | number;
  initialIndex?: number;
  items: T[];
  offset: number;
  onChange?: (index: number) => void;
  onEnter?: (item: T, index: number) => void;
  onExit?: () => void;
  onKeyDown?: () => void;
  onKeyUp?: () => void;
  renderItem: (item: T, index: number) => ReactNode;
}

/**
 * Custom hook to manage the circular list data and functions.
 * @param props - Props for the CircularList component.
 * @returns {Object} An object containing the circular list data and functions.
 */
export default function useCircularList<T>({
  direction = Direction.HORIZONTAL,
  duration: originalDuration,
  focus,
  initialIndex = 0,
  items,
  offset,
  onChange,
  onEnter,
  onExit,
  onKeyDown,
  onKeyUp,
  renderItem,
}: CircularListProps<T>) {
  // ------------------------------------------------------------
  // Initialization
  // ------------------------------------------------------------

  // Selectors
  const currentFocus = useAppSelector(selectFocus);

  const length = items?.length ?? 0;
  // A single item has nowhere to wrap to, so no duplicates and no circular navigation.
  const noDuplicates =
    DeviceConfig.capabilities.disableAnimations ||
    !originalDuration ||
    length <= 1;
  const circularLength = noDuplicates ? length : length + 2;

  // State: when no duplicates use logical index (0..length-1); otherwise internal index (0..length+1)
  const [duration, setDuration] = useState(originalDuration);
  const [index, setIndex] = useState(() => {
    const safe = Math.min(
      Math.max(0, initialIndex),
      Math.max(0, (items?.length ?? 0) - 1)
    );
    return noDuplicates ? safe : safe + 1;
  });

  // Variables
  const focused =
    focus === currentFocus ||
    (Array.isArray(currentFocus) &&
      ((currentFocus as (number | string)[]).includes(focus as number) ||
        (currentFocus as (number | string)[]).includes(String(focus))));

  // Refs
  const animatingRef = useRef<boolean>(false); // Whether the list is currently animating.
  const blockPressUntilRef = useRef<number>(0); // Prevents multiple key presses from moving the index multiple times.
  const circularLengthRef = useRef(circularLength);
  const focusedRef = useRef(focused); // Whether the list is currently focused.
  const indexRef = useRef<number>(index); // The current index.
  const itemsRef = useRef(items);
  const lastReportedRef = useRef<number>(initialIndex); // The last reported logical index.
  const lastMoveTimeRef = useRef<number>(0); // The last time the index was moved.
  const lengthRef = useRef(length);
  circularLengthRef.current = circularLength;
  focusedRef.current = focused;
  indexRef.current = index;
  itemsRef.current = items;
  lengthRef.current = length;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  // Converts a logical index to an internal index (no-op when there are no duplicates).
  const logicalToInternal = useCallback(
    (logical: number) =>
      noDuplicates ? logical : Math.min(logical + 1, length),
    [noDuplicates, length]
  );

  // Converts an internal index to a logical index (identity when there are no duplicates).
  const internalToLogical = useCallback(
    (internal: number) =>
      noDuplicates
        ? internal
        : internal === 0
        ? lengthRef.current - 1
        : internal === circularLengthRef.current - 1
        ? 0
        : internal - 1,
    [noDuplicates]
  );

  // Reports the current logical index when the index changes.
  const reportChange = useCallback(
    (logicalIndex: number) => {
      if (lastReportedRef.current === logicalIndex) return;
      lastReportedRef.current = logicalIndex;
      onChange?.(logicalIndex);
    },
    [onChange]
  );

  // Moves the index by a given delta. When there are no duplicates, index is logical and wraps 0..length-1.
  const moveByOffset = useCallback(
    (delta: number) => {
      if (!lengthRef.current) return;
      const len = lengthRef.current;
      setIndex((prev) => {
        if (noDuplicates) {
          return (prev + delta + len) % len;
        }
        let next = prev + delta;
        if (next === 0 && onExit) {
          onExit();
          animatingRef.current = false;
          return prev;
        }
        if (next < 0) next = circularLengthRef.current - 1;
        if (next > circularLengthRef.current - 1)
          next = circularLengthRef.current - 1;
        return next;
      });
    },
    [noDuplicates]
  );

  // Tries to move the index by a given key and pressures.
  const navigate = useCallback(
    (key: string, pressures: number) => {
      if (animatingRef.current) return;
      // A single item can't navigate anywhere.
      if (lengthRef.current <= 1) return;
      const now = Date.now();
      if (now < blockPressUntilRef.current) return;
      const firstPress = pressures <= 1;
      const throttleMs = originalDuration * 1000;
      const throttled =
        !firstPress &&
        throttleMs > 0 &&
        now - lastMoveTimeRef.current < throttleMs;
      if (throttled) return;
      const animationsDisabled = DeviceConfig.capabilities.disableAnimations;
      if (!animationsDisabled) animatingRef.current = true;
      lastMoveTimeRef.current = now;
      if (direction === Direction.HORIZONTAL) {
        if (key === 'RIGHT') moveByOffset(1);
        else if (key === 'LEFT') moveByOffset(-1);
      } else {
        if (key === 'DOWN') moveByOffset(1);
        else if (key === 'UP') moveByOffset(-1);
      }
    },
    [direction, originalDuration, moveByOffset]
  );

  // Handles the transition end event (no-op when there are no duplicates to snap).
  const onTransitionEnd = useCallback(() => {
    animatingRef.current = false;
    if (noDuplicates || !lengthRef.current) return;
    // Right wrap: was at right duplicate (length+1) -> snap to real first item (internal index: 1)
    if (index === circularLengthRef.current - 1) {
      setDuration(0);
      setIndex(1);
      lastReportedRef.current = 0;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
      return;
    }
    // Left wrap: was at left duplicate (0) -> snap to real last (internal length)
    if (index === 0) {
      setDuration(0);
      setIndex(lengthRef.current);
      lastReportedRef.current = lengthRef.current - 1;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
    }
  }, [noDuplicates, index, originalDuration, onChange]);

  // ------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------

  // When animations are disabled, transitionEnd never fires; run wrap-snap and clear logic after index updates.
  useEffect(() => {
    if (!DeviceConfig.capabilities.disableAnimations) return;
    onTransitionEnd();
  }, [index, onTransitionEnd]);

  // Syncs the index from the parent (e.g. timer / auto-advance).
  useEffect(() => {
    if (!length) return;
    const logical = Math.min(Math.max(0, initialIndex), length - 1);
    if (noDuplicates) {
      setIndex(logical);
      return;
    }
    const nextInternal = logicalToInternal(logical);
    setIndex((prev) => {
      if (prev === 0 || prev === circularLengthRef.current - 1) return prev;
      if (
        prev !== nextInternal &&
        prev === lengthRef.current &&
        nextInternal === 1
      )
        return circularLengthRef.current - 1;
      if (
        prev !== nextInternal &&
        prev === 1 &&
        nextInternal === lengthRef.current
      )
        return 0;
      return prev !== nextInternal ? nextInternal : prev;
    });
  }, [noDuplicates, length, initialIndex, logicalToInternal]);

  // Report current logical index when index changes; skip when no items to avoid reporting -1
  useEffect(() => {
    if (!lengthRef.current) return;
    const logical = internalToLogical(index);
    if (logical < 0 || logical >= lengthRef.current) return;
    reportChange(logical);
  }, [index, internalToLogical, reportChange]);

  // ------------------------------------------------------------
  // Keyboard
  // ------------------------------------------------------------

  // Checks if a key is a navigation key.
  const isNavKey = useCallback(
    (key: string) => {
      if (direction === Direction.HORIZONTAL)
        return key === 'LEFT' || key === 'RIGHT';
      return key === 'UP' || key === 'DOWN';
    },
    [direction]
  );

  // Handles the keyboard events.
  useKeyboard({
    focus,
    keyDownHandler: (key, _longPress, pressures) => {
      if (!focusedRef.current || !lengthRef.current) return;
      if (key === 'SELECT') {
        const logical = internalToLogical(indexRef.current);
        onEnter?.(itemsRef.current[logical], logical);
        return;
      }
      if (!isNavKey(key)) return;
      onKeyDown?.();
      navigate(key, pressures ?? 0);
    },
    keyUpHandler: (key) => {
      if (isNavKey(key)) onKeyUp?.();
    },
  });

  // ------------------------------------------------------------
  // Memoization
  // ------------------------------------------------------------

  // When there are no duplicates: items as-is. Otherwise: duplicate first/last for seamless wrap animation.
  const circularItems = useMemo(() => {
    if (!length) return [];
    return noDuplicates ? items : [items[length - 1], ...items, items[0]];
  }, [noDuplicates, items, length]);

  // Only the current item ± RENDER_WINDOW_RADIUS neighbors render; others become same-sized
  // placeholders (translateX math assumes every slot is `offset` wide/tall).
  //
  // Positions 0/1 and length/(circularLength-1) are the wrap-anchor pairs (duplicate <-> real
  // item depicting the same logical entry) and stay permanently mounted, so the wrap-snap
  // doesn't flash — a freshly-mounted item would start at opacity 0.
  const children = useMemo(() => {
    const placeholderSize = offset * Screen.widthRatio;
    const placeholderStyle =
      direction === Direction.HORIZONTAL
        ? { width: placeholderSize }
        : { height: placeholderSize };
    const lastIndex = circularItems.length - 1;
    return circularItems.map((item, i) => {
      const isWrapAnchor =
        !noDuplicates &&
        (i === 0 || i === 1 || i === length || i === lastIndex);
      const display =
        Math.abs(i - index) <= RENDER_WINDOW_RADIUS || isWrapAnchor;
      return (
        <Fragment key={i}>
          {display ? (
            renderItem(item, internalToLogical(i))
          ) : (
            <div style={placeholderStyle} />
          )}
        </Fragment>
      );
    });
  }, [
    circularItems,
    direction,
    index,
    length,
    noDuplicates,
    offset,
    internalToLogical,
    renderItem,
  ]);

  return {
    children,
    duration,
    index,
    onTransitionEnd,
  };
}
